// SecondaryPane — the right zone: the single summonable secondary, pure.
//
// .claude/research/design/02: empty and closed by default; when summoned it holds
// EXACTLY ONE of the slots {Assistant · Review · Quality}, never two at
// once. The head is DEMOTED (composition contract D2/I5): it names the
// active slot (title + optional subject line) and carries close + caller-
// keyed actions — it does NOT switch slots. The toolbar's summon group is
// the app's only slot-switching control and the only availability
// authority; two switchers disagreed once (V3) and never will again.
//
// The active slot is CONTROLLED (`slot` is an input); closing reports UP
// (`onClose`) — the shell folds it into the frame. The pane never holds its
// own selection or open state. The slot insides render caller-supplied
// `slotContent`, falling back to an EmptyState placeholder for any slot the
// caller leaves unwired.
//
// Composes existing primitives: IconButton for close, ScrollArea for the
// body, EmptyState for each placeholder. ARIA landmark role="complementary"
// (.claude/research/design/02). Owns its own strings (the slot titles + placeholder
// copy) via Lingui; `ariaLabel` names the landmark (caller-owned).
// Pure/props-driven; no I/O.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { SecondarySlot } from "../AppShell/AppShell.js";
import { secondarySlotLabels } from "../AppShell/secondarySlotLabels.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { ScrollArea } from "../ScrollArea/ScrollArea.js";
import "./SecondaryPane.css";

// How a slot's real content sits in the pane body — a named sum, not a flag:
//   scrolling — the pane wraps it in its own ScrollArea (a document-shaped
//               surface: quality read, proposal queue).
//   filling   — the content owns its own scrolling and fills the body (a
//               transcript with a pinned composer would double-nest inside
//               the pane's ScrollArea).
export type ScrollingSlotContent = {
    readonly type: "scrolling";
    readonly node: ReactNode;
};
export type FillingSlotContent = {
    readonly type: "filling";
    readonly node: ReactNode;
};
// A slot that draws its OWN head (title + close in one row): the pane renders
// no head of its own, so the slot never shows two header rows.
type OwnHeadSlotContent = {
    readonly type: "own-head";
    readonly node: ReactNode;
};
export type PaneSlotContent =
    | ScrollingSlotContent
    | FillingSlotContent
    | OwnHeadSlotContent;

export type SecondaryPaneProps = {
    // The active slot: names the head and picks the body.
    readonly slot: SecondarySlot;
    // The head's subject line — names the slot's object whenever it is not
    // the active tab (composition contract I6/D7; the shell supplies it).
    readonly subject?: string;
    readonly onClose: () => void;
    // Names the complementary landmark for AT (caller-owned / localised).
    readonly ariaLabel: string;
    // Optional real content per slot. When supplied for the ACTIVE slot the
    // body renders it instead of the placeholder EmptyState; slots left
    // absent keep their sketch copy. Lets the app seat a live surface (e.g.
    // the Grounding pane's QualitySummary) without every slot being wired.
    readonly slotContent?: Partial<Record<SecondarySlot, PaneSlotContent>>;
    readonly className?: string;
};

// The ONE registry close form (P33).
const closeGlyph = <Icon name="close" />;

export const SecondaryPane = ({
    slot,
    subject,
    onClose,
    ariaLabel,
    slotContent,
    className,
}: SecondaryPaneProps) => {
    const { t, i18n } = useLingui();
    // The slot's noun comes from the ONE shared source (secondarySlotLabels),
    // so the pane head and the summon group can never name a slot differently.
    const label = i18n._(secondarySlotLabels[slot]);
    // A placeholder BODY per slot — forced exhaustive by the Record, so a new
    // slot breaks compilation until its copy is written. The title reuses the
    // shared slot noun above; only the body is authored here. The insides are a
    // sketch; docs 03/04 seat the real surfaces here later.
    const placeholderBody: Record<SecondarySlot, ReactNode> = {
        assistant: t`Ask the assistant to make or change material.`,
        review: t`Pedagogical, linguistic, and factual axes — shown side by side, never averaged.`,
        grounding: t`The evidence behind a claim — the quote is the proof.`,
        panel: t`What this chat makes, and the sources it rests on.`,
    };
    const active = slotContent?.[slot];
    if (active?.type === "own-head") {
        return (
            <aside
                aria-label={ariaLabel}
                className={`bilda-secondary-pane${className ? ` ${className}` : ""}`}
            >
                <div className="bilda-secondary-pane__fill">{active.node}</div>
            </aside>
        );
    }

    return (
        <aside
            aria-label={ariaLabel}
            className={`bilda-secondary-pane${className ? ` ${className}` : ""}`}
        >
            <div className="bilda-secondary-pane__head">
                <div className="bilda-secondary-pane__heading">
                    <h2 className="bilda-secondary-pane__title">{label}</h2>
                    {subject !== undefined ? (
                        <span className="bilda-secondary-pane__subject">
                            {subject}
                        </span>
                    ) : null}
                </div>
                <div className="bilda-secondary-pane__head-actions">
                    <IconButton
                        label={t`Close panel`}
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                    >
                        {closeGlyph}
                    </IconButton>
                </div>
            </div>

            {active?.type === "filling" ? (
                <div className="bilda-secondary-pane__fill">{active.node}</div>
            ) : (
                <ScrollArea className="bilda-secondary-pane__body">
                    {active?.node ?? (
                        <EmptyState
                            title={label}
                            body={placeholderBody[slot]}
                        />
                    )}
                </ScrollArea>
            )}
        </aside>
    );
};
